/* eslint-disable */
import * as React from "react";
import { CircularProgress } from "@mui/material";
import Pagination from "../../../components/TablePagination";
import { SnackbarOrigin } from "@mui/material/Snackbar";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import { fetchKpiList } from "../redux/actionCreators";
import { visuallyHidden } from "@mui/utils";
import { type RootState } from "../../../store";
import { KpiListState } from "../redux/kpiListSlice";
import { type ConnectedProps, connect } from "react-redux";
import LanguageStrings from "../../../i18n/locales";
import AuthError from "../../../components/ErrorComponents/AuthError";
import { APIConfiguration } from "../../../utils/apiConfiguration";
import ActionComponent from "./ActionComponent";

const kpiDetails = LanguageStrings().ThermalIQStrings.KpiDetailsComponent;
const kpiType = LanguageStrings().ThermalIQStrings.KpiTypeComponent;
const customKpi = LanguageStrings().ThermalIQStrings.CreateCustomKpiComponent;
const authErrors = LanguageStrings().AuthErrors;
const httpStatus = APIConfiguration;

export interface State extends SnackbarOrigin {
  open: boolean;
}

interface Data {
  kpi_name: string;
  remarks: string;
}

export type Order = "asc" | "desc";
interface HeadCell {
  disablePadding: boolean;
  id: any;
  label: string;
  numeric: boolean;
  sortable: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "kpiName",
    numeric: false,
    disablePadding: false,
    label: kpiDetails.KpiName,
    sortable: true,
  },
  {
    id: "kpiType",
    numeric: false,
    disablePadding: false,
    label: kpiType.KpiType,
    sortable: false,
  },
  {
    id: "frequency",
    numeric: false,
    disablePadding: false,
    label: customKpi.Frequency,
    sortable: false,
  },
  {
    id: "state",
    numeric: false,
    disablePadding: false,
    label: kpiDetails.State,
    sortable: false,
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: kpiDetails.Action,
    sortable: false,
  },
];

function EnhancedTableHead(props: any): any {
  const { order, orderBy, onRequestSort, kpiList } = props;
  const createSortHandler =
    (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };
  // istanbul ignore else
  if (kpiList !== null && kpiList !== undefined) {
    return (
      <TableHead data-testid="table-header">
        <TableRow>
          {headCells.map((headCell: any) => (
            <StyledTableCell
              key={headCell.id}
              sx={{
                fontFamily: "Honeywell Sans Web",
                fontSize: "12px",
                fontWeight: 700,
                lineHeight: "16px",
                letterSpacing: "2px",
              }}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSortLabel
                sx={{
                  "& .MuiTableSortLabel-icon": {
                    opacity: 1,
                    marginLeft: "100px",
                  },
                }}
                hideSortIcon={!headCell.sortable}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={
                  headCell.sortable ? createSortHandler(headCell.id) : undefined
                }
                aria-label={headCell.label}
                data-testid="test-sort"
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: "#F0F0F0",
    fontWeight: "700",
    fontSize: 12,
    border: "2px solid #202020",
    padding: "5px 16px",
    fontFamily: "Honeywell Sans Web",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#d0d0d0",
    paddingLeft: "17px",
    borderBottom: "1px solid #202020",
    borderRight: 0,
    fontFamily: "Honeywell Sans Web",
  },
}));

// Define styled components for the text
const EnabledText = styled("span")`
  color: #68d560;
`;
const DisabledText = styled("span")`
  color: #ff644c;
`;
export interface Sort {
  order: Order;
  orderBy: string;
}

function KpiList(props: AllProps) {
  const [selectedKpi, setSelectedKpi] = React.useState(null);

  React.useEffect(() => {
    if (props.searchVal === "") {
      fetchAllkpiList();
    } else {
      props.performAction();
    }
  }, [props.page, props.sort]);

  const fetchAllkpiList = () => {
    // istanbul ignore else
    if (props.page >= 1) {
      props.fetchKpiList(
        props.page - 1,
        props.rowsPerPage,
        props.sort.order,
        props.sort.orderBy,
        true
      );
    }
  };

  const handleKpiSelect = (): void => {
    setSelectedKpi(null);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    newOrderBy: keyof Data
  ) => {
    const isAsc = props.sort.orderBy === newOrderBy && props.sort.order === "asc";
    const toggledOrder = isAsc ? "desc" : "asc";
    props.setSort({ order: toggledOrder, orderBy: newOrderBy });
  };

  // istanbul ignore else
  if (
    props.kpiList.kpiList !== null &&
    props.kpiList.kpiList !== undefined &&
    props.kpiList.httpStatus === httpStatus.SUCCESS_HTTP_STATUS &&
    props.kpiList.kpiCount > 0
  ) {
    return (
      <>
        <Box className="devices">
          <Paper
            sx={{
              width: "100%",
              mb: 2,
              backgroundColor: "#272727",
              boxShadow: "unset",
              color: "#F0F0F0",
              marginBottom: "0",
            }}
          >
            <TableContainer className="device-table">
              <Table
                sx={{ minWidth: 750, color: "#f0f0f0" }}
                aria-labelledby="tableTitle"
                size={"small"}
              >
                <EnhancedTableHead
                  order={props.sort.order}
                  orderBy={props.sort.orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={props.kpiList.kpiList.length}
                  kpiList={props.kpiList.kpiList}
                  kpiListCount={props.kpiList.kpiList.length}
                />
                <TableBody>
                  {props.kpiList.kpiList &&
                    props.kpiList.kpiList.map((row: any, index: any) => {
                      return (
                        <TableRow key={index}>
                          <StyledTableCell style={{ color: "#64C3FF", width: "16%" }}>
                            {row.kpiName}
                          </StyledTableCell>
                          <StyledTableCell style={{ width: "250px" }}>
                            {row.isAssetLevel ? kpiDetails.AssetLevel : kpiDetails.DeviceLevel}
                          </StyledTableCell>
                          <StyledTableCell style={{ width: "250px" }}>
                            {row.isAssetLevel ? `${row.frequency} minute` : kpiDetails.NA}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.isEnabled === null ? (
                              <span>{kpiDetails.NA}</span>
                            ) : row.isEnabled ? (
                              <EnabledText>{kpiDetails.Enable}</EnabledText>
                            ) : (
                              <DisabledText>{kpiDetails.Disable}</DisabledText>
                            )}
                          </StyledTableCell>
                          <StyledTableCell style={{ width: "250px" }}>
                            <ActionComponent
                              row={row}
                              fetchAllkpiList={fetchAllkpiList}
                              isKpiSelected={
                                selectedKpi === row.kpiDefinitionId
                              }
                              isAssetLevel={row.isAssetLevel}
                              setIsKpiSelected={handleKpiSelect}
                            />
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={props.kpiList.kpiCount}
              page={props.page}
              setPage={props.setPage}
              rowsPerPage={props.rowsPerPage}
              siblingCount={1}
              boundaryCount={1}
            />
          </Paper>
        </Box>
      </>
    );
  } else if (props.kpiList.isLoading) {
    return (
      <div className="CircularprogressAmi" data-testid="loading-spinner">
        <CircularProgress />
      </div>
    );
  } else if (props.kpiList.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage={authErrors.Unauthorized} />
      </div>
    );
  } else if (
    props.kpiList.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage={authErrors.AccessForbidden} />
      </div>
    );
  } else if (
    props.kpiList.kpiList !== null &&
    props.kpiList.kpiList !== undefined &&
    props.kpiList.kpiList.length === 0
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage={authErrors.NoDataPresent} />
      </div>
    );
  } else if (
    props.kpiList.loadingError?.includes(kpiDetails.NoKpiFound) ||
    props.kpiList.loadingError?.includes(kpiDetails.KpiNotFound)
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage={authErrors.NoDataPresent} />
      </div>
    );
  } else {
    return (
      <div className="authError">
        <AuthError
          errorMessage={authErrors.CanNotFetch}
          retry={fetchAllkpiList}
        />
      </div>
    );
  }
}
interface DispatchToProps {
  fetchKpiList: (
    page: number,
    size: number,
    sortdir: string,
    sortfield: string,
    filter: boolean
  ) => void;
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchKpiList: (
    page: number,
    size: number,
    sortdir: string,
    sortfield: string,
    filter: boolean
  ) => dispatch(fetchKpiList(page, size, sortdir, sortfield, filter)),
});

interface StateToProps {
  kpiList: KpiListState;
}

const mapStateToProps = (state: RootState): StateToProps => ({
  kpiList: state.kpiList,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
interface ParentProps {
  searchVal: string;
  performAction: () => void;
  rowsPerPage: number;
  page: number;
  sort: Sort;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSort: React.Dispatch<React.SetStateAction<Sort>>;
}
type AllProps = PropsFromRedux & ParentProps;

export default connector(KpiList);
